import React from 'react';
import { Badge, statusVariant } from '../ui';
import type { ContentItem, ContentOutline, JobStatus } from '../../types';

/**
 * Workflow state per outline row — mirrors the future `workflow_status` field
 * from GET /api/content/workqueue/ (companion issue #108).
 * TODO: remove client-side derivation once that endpoint ships.
 */
export type PipelineState =
  | 'needs_approval'
  | 'needs_item_approval'
  | 'needs_content'
  | 'needs_media'
  | 'ready_to_schedule'
  | 'scheduled'
  | 'published'
  | 'in_progress'
  | 'failed';

const getFormatIcon = (format: string) => {
  if (format === 'video') return '🎬';
  if (format === 'carousel') return '🗂️';
  return '🖼️';
};

type GenerateMediaOptions = {
  mediaIndex?: number;
  mediaId?: number;
  regenerateAll?: boolean;
  contentFormat?: string;
  generationModeOverride?: 'sequential_context' | 'parallel';
  force?: boolean;
};

type QueueItemProps = {
  outline: ContentOutline;
  item: ContentItem | undefined;
  state: PipelineState;
  generatingContent: boolean;
  multiSlideJobInFlight: boolean;
  generatingMediaIds: Set<number>;
  videoJobStatus: JobStatus | null;
  hasActiveContentJobForOutline: (_outlineId: number) => boolean;
  hasActiveMediaJobForOutline: (_outlineId: number) => boolean;
  handleApprove: (_id: number) => void | Promise<void>;
  handleApproveContentItem: (_id: number) => void | Promise<void>;
  handleGenerateContentItem: (_outlineId: number) => void;
  handleGenerateMedia: (_contentItemId: number, _options?: GenerateMediaOptions) => void | Promise<void>;
  handleOpenPublishModal: (_contentId: number) => void;
  selectOutline: (_outline: ContentOutline | null) => void;
  getPillarName: (_pillarId: number | null | undefined) => string;
  formatDate: (_value?: string) => string;
  handleUnarchive: (_outlineId: number) => void | Promise<void>;
  handleArchive: (_outlineId: number) => void | Promise<void>;
};

// ── Queue item renderer ────────────────────────────────────────────────────
export function QueueItem({
  outline,
  item,
  state,
  generatingContent,
  multiSlideJobInFlight,
  generatingMediaIds,
  videoJobStatus,
  hasActiveContentJobForOutline,
  hasActiveMediaJobForOutline,
  handleApprove,
  handleApproveContentItem,
  handleGenerateContentItem,
  handleGenerateMedia,
  handleOpenPublishModal,
  selectOutline,
  getPillarName,
  formatDate,
  handleUnarchive,
  handleArchive,
}: QueueItemProps) {
  const isGeneratingThisContent = generatingContent || hasActiveContentJobForOutline(outline.id);
  const isGeneratingThisMedia =
    multiSlideJobInFlight ||
    generatingMediaIds.size > 0 ||
    hasActiveMediaJobForOutline(outline.id);
  const isVideoItem = item?.content_format === 'video';
  const videoProgress = Math.max(0, Math.min(100, videoJobStatus?.progress ?? 0));

  let primaryCta: React.ReactNode = null;

  if (outline.is_archived) {
    primaryCta = null;
  } else if (state === 'needs_approval') {
    primaryCta = (
      <button
        className="cp-cta-btn cp-cta-btn--approve"
        onClick={(e) => {
          e.stopPropagation();
          void handleApprove(outline.id);
        }}
      >
        ✓ Approve Outline
      </button>
    );
  } else if (state === 'needs_item_approval' && item) {
    primaryCta = (
      <button
        className="cp-cta-btn cp-cta-btn--approve"
        onClick={(e) => {
          e.stopPropagation();
          void handleApproveContentItem(item.id);
        }}
      >
        ✓ Approve Content
      </button>
    );
  } else if (state === 'needs_content') {
    primaryCta = (
      <button
        className="cp-cta-btn cp-cta-btn--generate"
        disabled={isGeneratingThisContent}
        onClick={(e) => {
          e.stopPropagation();
          void handleGenerateContentItem(outline.id);
        }}
      >
        {isGeneratingThisContent ? 'Generating…' : '✨ Generate Content'}
      </button>
    );
  } else if (state === 'needs_media' && item) {
    primaryCta = (
      <button
        className="cp-cta-btn cp-cta-btn--generate"
        disabled={isGeneratingThisMedia}
        onClick={(e) => {
          e.stopPropagation();
          void handleGenerateMedia(item.id, { contentFormat: item.content_format });
        }}
      >
        {isGeneratingThisMedia && isVideoItem
          ? `🎬 Rendering… ${videoProgress}%`
          : isGeneratingThisMedia
            ? 'Generating…'
            : item.content_format === 'video'
              ? '🎬 Generate Video'
              : '🎨 Generate Media'}
      </button>
    );
  } else if (state === 'ready_to_schedule' && item) {
    primaryCta = (
      <button
        className="cp-cta-btn cp-cta-btn--schedule"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenPublishModal(item.id);
        }}
      >
        📤 Upload Now
      </button>
    );
  }

  return (
    <div
      key={outline.id}
      className="cp-item"
      onClick={() => selectOutline(outline)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') selectOutline(outline);
      }}
    >
      <div className="cp-item__format-icon">{getFormatIcon(outline.content_format)}</div>

      <div className="cp-item__body">
        <div className="cp-item__title">{outline.title}</div>
        {outline.summary && <div className="cp-item__summary">{outline.summary}</div>}
        <div className="cp-item__meta">
          {outline.pillar && (
            <span className="cp-item__meta-tag">
              🏛️ {getPillarName(outline.pillar) || 'Unknown pillar'}
            </span>
          )}
          {outline.publish_at && (
            <span className="cp-item__meta-tag">📅 {formatDate(outline.publish_at)}</span>
          )}
          <Badge variant={statusVariant(outline.status)}>Outline: {outline.status}</Badge>
          {item && <Badge variant={statusVariant(item.status)}>Content: {item.status}</Badge>}
          {(isGeneratingThisContent || isGeneratingThisMedia) && (
            <span className="cp-item__ai-running" title="AI job is running for this post">
              ⚡ AI running…
            </span>
          )}
        </div>
      </div>

      <div className="cp-item__actions" onClick={(e) => e.stopPropagation()}>
        {primaryCta}
        <button
          className="cp-cta-btn cp-cta-btn--ghost"
          onClick={(e) => {
            e.stopPropagation();
            if (outline.is_archived) {
              void handleUnarchive(outline.id);
            } else {
              void handleArchive(outline.id);
            }
          }}
          title={outline.is_archived ? 'Restore to active pipeline' : 'Archive post'}
          aria-label={outline.is_archived ? 'Restore to active pipeline' : 'Archive post'}
        >
          {outline.is_archived ? '↩︎ Restore' : '🗂 Archive'}
        </button>
      </div>
    </div>
  );
}
